import React, { useCallback, useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';
import { forTemplate } from '@trello/i18n';
import { LeaveBoardIcon } from '@trello/nachos/icons/leave-board';

import { useChangeCardRoleMutation } from './ChangeCardRoleMutation.generated';
import { useConvertCardRoleButtonQuery } from './ConvertCardRoleButtonQuery.generated';
import { QuickCardEditorButton } from './QuickCardEditorButton';
import { useRefetchConvertCardRoleButtonQuery } from './useRefetchConvertCardRoleButtonQuery';

const format = forTemplate('quick_card_editor');

interface ConvertCardRoleButtonProps {
  idCard: string;
  onLoad: () => void;
  analyticsContainers: Parameters<
    typeof Analytics.sendClickedButtonEvent
  >[0]['containers'];
}

export const ConvertCardRoleButton = ({
  idCard,
  onLoad,
  analyticsContainers,
}: ConvertCardRoleButtonProps) => {
  const [changeCardRole] = useChangeCardRoleMutation();
  const { data, loading } = useConvertCardRoleButtonQuery({
    variables: {
      idCard,
    },
    waitOn: ['PreloadCard'],
  });

  // This is used to recalculate the height of the quick edit
  // menu after the button gets rendered. This will likely
  // need to be here until the quick edit menu gets rewritten
  // in the new stack, since as of now, this button gets rendered
  // *after* the menu is opened. Without this code, there is
  // a chance that the ConvertCardRoleButton will be off screen
  // and unable to be seen or clicked on
  useEffect(onLoad, [onLoad, data]);

  // Because fetching possibleCardRole is done on a separate
  // endpoint from other fields on the card, we must refetch
  // the value whenever the card model is changed in order to
  // ensure that we don't have stale data.
  useRefetchConvertCardRoleButtonQuery(idCard);

  let cardRole = data?.card?.cardRole;
  let possibleCardRole = data?.card?.possibleCardRole;

  const canBeSeparatorCard = possibleCardRole === 'separator';

  if (canBeSeparatorCard) {
    possibleCardRole = null;
    cardRole = null;
  }

  const canBeBoardCard = possibleCardRole === 'board';
  const isBoardCardEnabled = featureFlagClient.get(
    'wildcard.board-cards',
    false,
  );

  if (!isBoardCardEnabled && canBeBoardCard) {
    possibleCardRole = null;
    cardRole = null;
  }

  const canBeLinkCard = possibleCardRole === 'link';

  const trackClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'convertCardButton',
      source: 'quickCardEditorInlineDialog',
      attributes: {
        fromCardRole: cardRole,
        toCardRole: cardRole ? null : possibleCardRole,
      },
      containers: analyticsContainers,
    });
  }, [analyticsContainers, cardRole, possibleCardRole]);

  const trackConversion = useCallback(
    (fromCardRole, toCardRole) => {
      Analytics.sendTrackEvent({
        action: 'converted',
        actionSubject: 'card',
        source: 'quickCardEditorInlineDialog',
        attributes: {
          fromCardRole,
          toCardRole,
        },
        containers: analyticsContainers,
      });
    },
    [analyticsContainers],
  );

  const convertCard = useCallback(() => {
    trackClick();

    if (cardRole) {
      changeCardRole({
        variables: {
          idCard,
          cardRole: null,
        },
      }).then(() => trackConversion(cardRole, null));
    } else if (canBeBoardCard) {
      changeCardRole({
        variables: {
          idCard,
          cardRole: 'board',
        },
      }).then(() => trackConversion(null, 'board'));
    } else if (canBeSeparatorCard) {
      changeCardRole({
        variables: {
          idCard,
          cardRole: 'separator',
        },
      }).then(() => trackConversion(null, 'separator'));
    } else if (canBeLinkCard) {
      changeCardRole({
        variables: {
          idCard,
          cardRole: 'link',
        },
      }).then(() => trackConversion(null, 'link'));
    }
  }, [
    trackClick,
    cardRole,
    canBeBoardCard,
    canBeSeparatorCard,
    canBeLinkCard,
    changeCardRole,
    idCard,
    trackConversion,
  ]);

  useEffect(() => {
    if (cardRole === 'mirror') {
      convertCard();
    }
  }, [cardRole, convertCard]);

  if (loading || (!cardRole && !possibleCardRole)) {
    return null;
  }

  return (
    <QuickCardEditorButton
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (see quick-card-editor.less)
      icon={<LeaveBoardIcon color="#c7d1db" size="small" />}
      onClick={convertCard}
    >
      {!!cardRole && format('convert-to-regular-card')}
      {!cardRole && canBeBoardCard && format('convert-to-board-card')}
      {!cardRole && canBeSeparatorCard && format('convert-to-separator-card')}
      {!cardRole && canBeLinkCard && format('convert-to-link-card')}
    </QuickCardEditorButton>
  );
};
